import { AnimationBaseController, AnimationTextStyle, IAnimationControllerConfiguration, Inject } from "@denishussar/html5-game-engine";
import { Graphics } from "pixi.js";
import { ButtonController, IButtonControllerOptions } from "../../Common/ButtonController";
import { getPaintConfig, getUpgradeConfig } from "../../Common/GameConfig";
import { GarageEvents } from "../../Constants/Events";
import { COIN, COIN56, COIN64, COIN70 } from "../../Constants/ImageConstants";
import { LocalizationContents } from "../../LocalizationContents";
import { GameLogic } from "../../Logic/GameLogic";
import { GarageLogic } from "../../Logic/GarageLogic";
import { CarModel } from "../../Models/CarModel";
import { AppStateEnum, GameModel } from "../../Models/GameModel";
import { shorterNumber, thousandSpacing } from "../../Utils/StringRework";
import { SinkaTweener } from "../../Utils/Tweener";

export interface IGarageMainButtonOptions { }

enum StateEnum {
    buy,
    go,
    repair,
}

export class GarageMainButton extends AnimationBaseController {
    $model: GameModel;
    tweener: SinkaTweener;
    defaultScale: number;
    constructor(
        @Inject private options: IGarageMainButtonOptions,
        @Inject private logic: GameLogic,
        @Inject private garageLogic: GarageLogic,
    ) {
        super("GarageMainButton");
    }

    getConfiguration(): IAnimationControllerConfiguration {
        const events = {};
        events[GarageEvents.buyCar] = () => this.updateMainButton();
        events[GarageEvents.buyPaint] = () => this.updateMainButton();
        events[GarageEvents.buyUpgrade] = () => this.updateMainButton();
        events[GarageEvents.repairCar] = () => this.updateMainButton();

        return {
            modelEvents: events,
            modelWatch: {
                coins: (value: number) => {
                    this.updateMainButton();
                },
                garage_selectedUpgrade: (upgrade: number) => {
                    this.updateMainButton();
                },
                garage_selectedPaint: (paint: number) => {
                    this.updateMainButton();
                },
                currentPlayer: (player: CarModel) => {
                    this.updateMainButton(player);
                    this.$model.garage_selectedPaint = player.paint;
                },
                garage_currentTab: (index: number) => {
                    this.updateMainButton();
                },
            },
            childControllers: {
                btn_go: {
                    instanceName: 'btn_go_garage',
                    controller: ButtonController,
                    options: {
                        onClick: () => {
                            this.logic.setState(AppStateEnum.levelSelector);
                        }
                    }
                },
                btn_repair: {
                    instanceName: 'btn_repair_garage',
                    controller: ButtonController,
                    options: {
                        onClick: () => {
                            this.garageLogic.repairCar();
                            // this.updateMainButton();
                            // this.updateCars();
                        }
                    }
                },
                btn_buy: {
                    instanceName: 'btn_buy_garage',
                    controller: ButtonController,
                    model: this.$model,
                    options: <IButtonControllerOptions>{
                        onClick: () => {
                            switch (this.$model.garage_currentTab) {
                                case 0:
                                    this.garageLogic.buyCar();
                                    break;
                                case 1:
                                    this.garageLogic.buyUpgrade();
                                    break;
                                case 2:
                                    this.garageLogic.buyPaint();
                                    break;
                            }

                            this.updateMainButton();
                        },
                    },
                },
            }
        }
    }

    private curve(duration: number, estimate: number, init: number, end: number) {
        return init + (end - init) * Math.pow(Math.sin((duration - estimate) / duration * Math.PI), 2);
    }

    private animateButton() {
        this.tweener.destroy();
        this.tweener = new SinkaTweener(this);

        this.tweener.animateTo(
            {
                scaleX: this.defaultScale * 1.2,
                scaleY: this.defaultScale * 1.2,
            },
            0.6,
            () => { },
            this.curve
        )
    }
    // private debug ;
    public updateMainButton(player = this.$model.currentPlayer) {
        this.localise();
        this.visible = true;
        this.tweener.stop();
        this.scaleX = this.scaleY = this.defaultScale;

        if (this.$model.currentPlayer.health != 100) {
            this.gotoAndStop(StateEnum.repair);
            this.setRepairText(this.garageLogic.calcRepairPrice());
            this.setTextByQuery("btn_repair_garage.button_garage_repair_txt", LocalizationContents.getText("button_garage_repair"));
            return;
        }

        switch (this.$model.garage_currentTab) {
            case 0:
                if (!player.isBought) {
                    this.setBuyText(player.price);
                    this.gotoAndStop(StateEnum.buy);
                    this.animateButton()
                }
                else {
                    if (this.$model.currentPlayer.health == 100)
                        this.gotoAndStop(StateEnum.go);
                    else {
                        this.gotoAndStop(StateEnum.repair);
                        this.setRepairText(this.garageLogic.calcRepairPrice());
                    }


                }
                break;
            case 1:
                if ((player.isBought)) {
                    if ((!player.upgrades[this.$model.garage_selectedUpgrade])) {
                        this.setBuyText(+getUpgradeConfig(this.$model.garage_selectedUpgrade, 'price'));
                        this.gotoAndStop(StateEnum.buy);
                        this.animateButton();
                    }
                    else
                        this.gotoAndStop(StateEnum.go)
                }
                else {
                    this.visible = false;
                }
                break;
            case 2:
                if ((player.isBought)) {
                    if (player.paint == this.$model.garage_selectedPaint) {
                        this.gotoAndStop(StateEnum.go)
                        break;
                    }
                    if (!player.availablePaints[this.$model.garage_selectedPaint]) {
                        this.setBuyText(getPaintConfig(this.$model.garage_selectedPaint, 'price'))
                        this.gotoAndStop(StateEnum.buy);
                        this.animateButton();
                    }
                }
                else {
                    this.visible = false;
                }
                break;
        }

        // this.setTextVerticalAlignByQuery("btn_buy_garage.buy_txt", "top");

    }

    setBuyText(n: number) {
        if (n < 100000) {
            this.setTextByQuery('btn_buy_garage.buy_txt_active', "<coin> </coin>    " + thousandSpacing(n) + " "); //
            this.setTextByQuery('btn_buy_garage.buy_txt_hover', "<coin> </coin>    " + thousandSpacing(n) + "");
            this.setTextByQuery('btn_buy_garage.buy_txt_press', "<coin> </coin>   " + thousandSpacing(n) + " ");
        } //
        else {
            this.setTextByQuery('btn_buy_garage.buy_txt_active', "<coin> </coin>    " + shorterNumber(n) + " ");
            this.setTextByQuery('btn_buy_garage.buy_txt_hover', "<coin> </coin>    " + shorterNumber(n) + "");
            this.setTextByQuery('btn_buy_garage.buy_txt_press', "<coin> </coin>   " + shorterNumber(n) + " ");

        }
    }

    setRepairText(n: number) {
        if (n < 100000) {
            this.setTextByQuery('btn_repair_garage.repair_txt_active', "<coin> </coin>    " + thousandSpacing(n) + " "); //
            this.setTextByQuery('btn_repair_garage.repair_txt_hover', "<coin> </coin>    " + thousandSpacing(n) + "");
            this.setTextByQuery('btn_repair_garage.repair_txt_press', "<coin> </coin>   " + thousandSpacing(n) + " ");
        } //
        else {
            this.setTextByQuery('btn_repair_garage.repair_txt_active', "<coin> </coin>    " + shorterNumber(n) + " ");
            this.setTextByQuery('btn_repair_garage.repair_txt_hover', "<coin> </coin>    " + shorterNumber(n) + "");
            this.setTextByQuery('btn_repair_garage.repair_txt_press', "<coin> </coin>   " + shorterNumber(n) + " ");

        }
    }

    private localise() {
        this.setTextByQuery("btn_buy_garage.button_garage_buy", LocalizationContents.getText("button_garage_buy"));
        this.setTextByQuery("btn_go_garage.button_garage_ready", LocalizationContents.getText("button_garage_ready"));
        this.setTextByQuery("btn_repair_garage.button_garage_repair", LocalizationContents.getText("button_garage_repair"));
        this.setTextVerticalAlignByQuery("btn_buy_garage.buy_txt", "middle");
        this.setTextVerticalAlignByQuery("btn_repair_garage.repair_txt", "middle");
        this.setTextVerticalAlignByQuery("btn_buy_garage.button_garage_buy", "middle");
        this.setTextVerticalAlignByQuery("btn_go_garage.button_garage_ready", "middle");
        this.setTextVerticalAlignByQuery("btn_repair_garage.button_garage_repair", "middle");
    }

    onInitialize() {
        this.defaultScale = this.scaleX;
        this.tweener = new SinkaTweener(this);
        this.updateMainButton();

        this.setStyles()
    }

    private setStyles() {

        const coin64: AnimationTextStyle = {
            imgSrc: COIN64,
            height: 56,
            width: 58,
            valign: "middle",
            padding: 10,
            fontSize: 64,
        }
        const coin70: AnimationTextStyle = {
            imgSrc: COIN70,
            height: 60,
            width: 70,
            valign: "middle",
            padding: 10,
            fontSize: 70,
        }

        const coin56:AnimationTextStyle = {
            imgSrc: COIN56,
            height: 54,
            width: 56,
            valign: "middle",
            padding: 10,
            fontSize: 70,
        }

        this.setTextStyleByQuery("btn_buy_garage.buy_txt_active", {valign: "middle"}, {
            coin: coin64,
        })
        this.setTextStyleByQuery("btn_buy_garage.buy_txt_hover", {valign: "middle"}, {
            coin: coin70,
        })
        this.setTextStyleByQuery("btn_buy_garage.buy_txt_press", { valign: "middle" }, {
            coin: coin56,
        })
        this.setTextStyleByQuery("btn_repair_garage.repair_txt_active", {valign: "middle"}, {
            coin: coin64,
        })
        this.setTextStyleByQuery("btn_repair_garage.repair_txt_hover", {valign: "middle"}, {
            coin: coin70,
        })
        this.setTextStyleByQuery("btn_repair_garage.repair_txt_press", { valign: "middle" }, {
            coin: coin56,

        })
    }
}